import { boxShadows, corners, margin, marginBottom, media, padding, typography } from "../../common/styles";
import React, { useCallback, useContext, useRef } from "react";
import styled, { css } from "styled-components";
import { ActionType } from "../../AppDataProvider/actions";
import { AppContext } from "../../AppDataProvider/AppDataProvider";
import { applyOpacity } from "../../common/styles/utils";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LanguageSelector } from "../../common/components";
import { NonModalDialogVariant } from "../../AppDataProvider/types";
import TimezoneList from "../../Steps/Availability/TimezoneSelector/TimezoneList/TimezoneList";
import { useOnClickOutside } from "./useOnClickOutside/useOnClickOutside";
import { useTranslation } from "react-i18next";
const contentMap: Record<NonModalDialogVariant, React.ReactNode> = {
  languageSelector: <LanguageSelector />,
  timezoneSelector: <TimezoneList />
};
const titleMap: Record<NonModalDialogVariant, string> = {
  languageSelector: "selectLanguage",
  timezoneSelector: "changeTimezone"
};
export function NonModalDialog() {
  const {
    t
  } = useTranslation();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  const onClose = useCallback(() => {
    appDataDispatch({
      type: ActionType.HIDE_NON_MODAL_DIALOG
    });
  }, [appDataDispatch]);
  const ref = useRef<HTMLDivElement>(null);
  const onClickOutsideCallback = useCallback(() => onClose(), [onClose]);
  useOnClickOutside(ref, onClickOutsideCallback);
  const variant = appData.nonModalDialog;
  if (typeof variant === "undefined") throw new Error("Cannot show nonModalDialog when variant is undefined");
  const title = t(titleMap[variant]);
  const content = contentMap[variant];
  return <Container ref={ref} variant={variant} data-sentry-element="Container" data-sentry-component="NonModalDialog" data-sentry-source-file="NonModalDialog.tsx">
      <TopRow data-sentry-element="TopRow" data-sentry-source-file="NonModalDialog.tsx">
        <Title data-sentry-element="Title" data-sentry-source-file="NonModalDialog.tsx">{title}</Title>
        <CloseButton aria-label={t("closeDialog")} onClick={onClose} data-sentry-element="CloseButton" data-sentry-source-file="NonModalDialog.tsx">
          <CloseIcon data-sentry-element="CloseIcon" data-sentry-source-file="NonModalDialog.tsx" />
        </CloseButton>
      </TopRow>
      <Content data-sentry-element="Content" data-sentry-source-file="NonModalDialog.tsx">{content}</Content>
    </Container>;
}
const Container = styled.div<{
  variant: NonModalDialogVariant;
}>`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 120px;
  left: 50%;
  -webkit-transform: translate(-50%, 0);
  transform: translate(-50%, 0);
  z-index: 1;
  max-height: 540px;
  max-width: min(32.5rem, 100%);
  min-width: min(20rem, 100%);
  ${({
  variant
}) => variant === "timezoneSelector" ? css`
          height: 100%;
        ` : null}
  ${boxShadows.subtleShadow}
  ${corners.xl}
  ${padding.l}
  background: ${({
  theme
}) => theme.colours.white};

  @media ${media.tablet} {
    max-height: 560px;
  }

  @media ${media.desktop} {
    max-height: 580px;
  }
`;
const TopRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  ${marginBottom.l}
`;
const Title = styled.h4`
  ${typography.subtitle}
  ${margin.zero}
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.8, false)};
`;
const CloseButton = styled.button`
  border: none;
  background: none;
  ${padding.zero}

  display: inline-flex;
  justify-content: flex-end;
  align-items: end;
  height: 1.25rem;
  width: 1.25rem;

  @media ${media.hoverable} {
    &:hover {
      cursor: pointer;
    }
  }
`;
const CloseIcon = styled(FontAwesomeIcon).attrs({
  icon: faTimes
})`
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.8, false)};
  font-size: 1rem;
`;
const Content = styled.div`
  ${typography.body}
  overflow: scroll;
  height: 100%;
`;
export default NonModalDialog;